import React, { useMemo, useState } from "react";
import {
  Dialog,
  DialogContent,
  Typography,
  Button,
  Box,
  IconButton,
  CircularProgress,
  Alert,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import CreditCardIcon from "@mui/icons-material/CreditCard";
import { loadStripe } from "@stripe/stripe-js";
import {
  Elements,
  ExpressCheckoutElement,
  PaymentElement,
  useStripe,
  useElements,
} from "@stripe/react-stripe-js";

// ─── PayBito Apex (Stripe) — hardcoded publishable key used for the
// "Fund Wallet" card-funding flow across the app. ───────────────────────────
const STRIPE_PUBLISHABLE_KEY =
  "pk_live_51UVm3fPbwdA4fAd3VIwva7KF2Jo7Xs2fcHpZ45xydY7Im7s9sLELBKFiQmk7fpPilKcxpn92wTkoKBqe3uJtJ4LH00ChVFW4dw";

let stripeFundWalletPromise = null;
const getStripeFundWalletPromise = () => {
  if (!stripeFundWalletPromise) {
    stripeFundWalletPromise = loadStripe(STRIPE_PUBLISHABLE_KEY);
  }
  return stripeFundWalletPromise;
};

const darkColors = {
  background: "#121212",
  paper: "#1e1e1e",
  text: "#ffffff",
  textSecondary: "#b0b0b0",
  primary: "#90caf9",
  border: "#333333",
  surface: "#1e1e1e",
  surfaceAlt: "#2a2a2a",
};

const lightColors = {
  background: "#ffffff",
  paper: "#f8f9fa",
  text: "#212529",
  textSecondary: "#6c757d",
  primary: "#1976d2",
  border: "#dee2e6",
  surface: "#f8f9fa",
  surfaceAlt: "#eef2f7",
};

// ─── Inner form — needs Elements context (useStripe/useElements) ───────────
const StripeFundWalletForm = ({
  colors,
  isDark,
  clientSecret,
  onError,
  onSuccess,
  processing,
  setProcessing,
}) => {
  const stripe = useStripe();
  const elements = useElements();
  const [expressAvailable, setExpressAvailable] = useState(false);

  // Confirms the real PaymentIntent created server-side via
  // /transaction/generateStripePaymentIntent (see PabtekPaymentPopup.js'
  // handleProceed, which fetches `clientSecret` before opening this popup).
  const handlePay = async () => {
    if (!stripe || !elements || !clientSecret) return;
    setProcessing(true);
    onError("");

    try {
      const { error: submitError } = await elements.submit();
      if (submitError) {
        onError(submitError.message || "Please check your payment details.");
        setProcessing(false);
        return;
      }

      const { error, paymentIntent } = await stripe.confirmPayment({
        elements,
        clientSecret,
        confirmParams: {
          return_url: `${window.location.origin}/my-wallet`,
        },
        redirect: "if_required",
      });

      if (error) {
        onError(error.message || "Payment failed. Please try again.");
        setProcessing(false);
        return;
      }

      onSuccess(paymentIntent);
    } catch (err) {
      onError("Something went wrong while processing your payment.");
      setProcessing(false);
    }
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      <ExpressCheckoutElement
        onConfirm={handlePay}
        onReady={({ availablePaymentMethods }) => {
          setExpressAvailable(!!availablePaymentMethods);
        }}
        onLoadError={(e) =>
          console.warn("[Stripe] ExpressCheckoutElement load error:", e)
        }
        options={{
          buttonHeight: 44,
          buttonTheme: { applePay: "black", googlePay: "black" },
        }}
      />

      {expressAvailable && (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <Box sx={{ flex: 1, height: "1px", backgroundColor: colors.border }} />
          <Typography
            variant="caption"
            sx={{ color: colors.textSecondary, whiteSpace: "nowrap" }}
          >
            or pay with
          </Typography>
          <Box sx={{ flex: 1, height: "1px", backgroundColor: colors.border }} />
        </Box>
      )}

      <PaymentElement
        onReady={() => console.log("[Stripe] PaymentElement ready")}
        onLoadError={(e) => {
          console.error("[Stripe] PaymentElement load error:", e);
          onError(
            e?.error?.message ||
            "Unable to load the payment form. Please try again."
          );
        }}
        options={{
          layout: "accordion",
          // Link and Amazon Pay are express/wallet methods surfaced above via
          // ExpressCheckoutElement — only list the accordion-row methods here.
          paymentMethodOrder: ["card", "us_bank_account", "cashapp", "klarna"],
        }}
      />

      <Button
        onClick={handlePay}
        variant="contained"
        disabled={!stripe || !elements || !clientSecret || processing}
        fullWidth
        endIcon={
          processing ? (
            <CircularProgress size={16} sx={{ color: "inherit" }} />
          ) : undefined
        }
        sx={{
          borderRadius: "12px",
          py: 1.5,
          fontWeight: 700,
          fontSize: "0.95rem",
          letterSpacing: "0.06em",
          textTransform: "uppercase",
          background:
            stripe && elements && clientSecret && !processing
              ? "linear-gradient(135deg, #1565c0 0%, #1976d2 100%)"
              : undefined,
          boxShadow:
            stripe && elements && clientSecret && !processing
              ? "0 4px 16px rgba(25,118,210,0.35)"
              : "none",
          "&:hover": {
            background: "linear-gradient(135deg, #0d47a1 0%, #1565c0 100%)",
            boxShadow: "0 6px 20px rgba(25,118,210,0.45)",
          },
          "&.Mui-disabled": {
            backgroundColor: isDark ? "#2a2a2a" : "#e0e0e0",
            color: isDark ? "#555" : "#9e9e9e",
          },
        }}
      >
        {processing ? "Processing…" : "Fund Wallet"}
      </Button>
    </Box>
  );
};

/**
 * StripeFundWalletPopup — common/reusable Stripe Elements popup for the
 * "Fund Wallet" card-funding flow. Shows the PayBito Apex (Stripe) express
 * checkout buttons (Link, Amazon Pay, Apple Pay, Google Pay where available)
 * plus the full Payment Element list (Card, Bank, Cash App Pay, Klarna), all
 * driven by the real `clientSecret` of a PaymentIntent created server-side
 * via /transaction/generateStripePaymentIntent (see PabtekPaymentPopup.js'
 * handleProceed). Clicking "Fund Wallet" calls stripe.confirmPayment with
 * that clientSecret to actually process the charge.
 *
 * Props:
 *   open          - boolean
 *   onClose       - function — close without completing payment
 *   onSuccess     - function(paymentIntent) — called after a successful charge
 *   theme         - "light" | "dark"
 *   currencyCode  - string, e.g. "USDC" — wallet asset credited
 *   clientSecret  - string — the Stripe PaymentIntent client secret
 *   amountUsd     - number — total USD amount charged to the card
 *   cryptoAmount  - number — amount of currencyCode credited to the wallet
 */
const StripeFundWalletPopup = ({
  open,
  onClose,
  onSuccess,
  theme = "light",
  currencyCode,
  clientSecret,
  amountUsd,
  cryptoAmount,
}) => {
  const muiTheme = useTheme();
  const isMobile = useMediaQuery(muiTheme.breakpoints.down("sm"));
  const isDark = theme === "dark";
  const colors = isDark ? darkColors : lightColors;

  const [processing, setProcessing] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");

  const logoUrl = `https://brokersexchange.s3.us-west-1.amazonaws.com/currency_logo/${(
    currencyCode || ""
  ).toUpperCase()}.png`;

  const elementsOptions = useMemo(
    () => ({
      clientSecret,
      appearance: {
        theme: isDark ? "night" : "stripe",
        variables: {
          colorPrimary: colors.primary,
          colorBackground: colors.surfaceAlt,
          colorText: colors.text,
          fontFamily: "Roboto, sans-serif",
          borderRadius: "10px",
        },
      },
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [clientSecret, isDark]
  );

  const handleClose = () => {
    if (processing) return;
    setErrorMsg("");
    onClose();
  };

  const handleSuccess = (paymentIntent) => {
    setProcessing(false);
    if (typeof onSuccess === "function") onSuccess(paymentIntent);
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      fullScreen={isMobile}
      scroll="paper"
      PaperProps={{
        sx: {
          borderRadius: isMobile ? 0 : "20px",
          backgroundColor: colors.background,
          color: colors.text,
          boxShadow: isDark
            ? "0 24px 64px rgba(0,0,0,0.6)"
            : "0 24px 64px rgba(0,0,0,0.12)",
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
          maxHeight: isMobile ? "100vh" : "92vh",
        },
      }}
    >
      {/* ── Gradient header band ─────────────────────────────── */}
      <Box
        sx={{
          background: isDark
            ? "linear-gradient(135deg, #1a237e 0%, #0d47a1 100%)"
            : "linear-gradient(135deg, #1565c0 0%, #1976d2 100%)",
          px: 3,
          pt: 3,
          pb: 3,
          position: "relative",
          flexShrink: 0,
        }}
      >
        <IconButton
          onClick={handleClose}
          size="small"
          sx={{
            position: "absolute",
            top: 12,
            right: 12,
            color: "rgba(255,255,255,0.7)",
            "&:hover": { backgroundColor: "rgba(255,255,255,0.12)", color: "#fff" },
          }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>

        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: "12px",
              backgroundColor: "rgba(255,255,255,0.15)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <CreditCardIcon sx={{ color: "#fff", fontSize: 22 }} />
          </Box>
          <Box>
            <Typography
              variant="h6"
              sx={{ color: "#fff", fontWeight: 700, lineHeight: 1.2 }}
            >
              Fund Your Wallet
            </Typography>
            <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.65)" }}>
              Secure Card Payment via PayBito Apex
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* ── Scrollable content ───────────────────────────────── */}
      <DialogContent sx={{ px: 3, pt: 3, pb: 0, overflowY: "auto", flex: 1 }}>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
          {/* Summary */}
          <Box
            sx={{
              borderRadius: "12px",
              border: `1px solid ${colors.border}`,
              overflow: "hidden",
            }}
          >
            {[
              {
                label: "Total Card Charge",
                value: `$${(amountUsd || 0).toLocaleString(undefined, {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })} USD`,
                highlight: true,
              },
              {
                label: "Wallet Asset Credited",
                value: `${(cryptoAmount || 0).toLocaleString(undefined, {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 8,
                })} ${currencyCode}`,
              },
              {
                label: "Wallet",
                value: `${currencyCode} Wallet`,
              },
            ].map((row, i, arr) => (
              <Box
                key={row.label}
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  px: 2,
                  py: 1.1,
                  backgroundColor: row.highlight
                    ? isDark
                      ? "rgba(144,202,249,0.1)"
                      : "rgba(25,118,210,0.07)"
                    : i % 2 === 0
                      ? colors.surfaceAlt
                      : colors.background,
                  borderBottom:
                    i < arr.length - 1 ? `1px solid ${colors.border}` : "none",
                }}
              >
                <Typography
                  variant="body2"
                  sx={{ color: colors.textSecondary, fontSize: "0.82rem" }}
                >
                  {row.label}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    color: row.highlight ? colors.primary : colors.text,
                    fontWeight: row.highlight ? 700 : 600,
                    fontSize: "0.82rem",
                  }}
                >
                  {row.value}
                </Typography>
              </Box>
            ))}
          </Box>

          {/* ── Stripe payment form ─────────────────────────────── */}
          {open && clientSecret ? (
            <Elements
              key={clientSecret}
              stripe={getStripeFundWalletPromise()}
              options={elementsOptions}
            >
              <StripeFundWalletForm
                colors={colors}
                isDark={isDark}
                clientSecret={clientSecret}
                onError={setErrorMsg}
                onSuccess={handleSuccess}
                processing={processing}
                setProcessing={setProcessing}
              />
            </Elements>
          ) : (
            open && (
              <Box sx={{ textAlign: "center", py: 3 }}>
                <CircularProgress size={22} sx={{ color: colors.primary }} />
                <Typography
                  variant="body2"
                  sx={{ color: colors.textSecondary, mt: 1, fontSize: "0.82rem" }}
                >
                  Loading secure payment form…
                </Typography>
              </Box>
            )
          )}

          {errorMsg && (
            <Alert
              severity="error"
              sx={{
                borderRadius: "10px",
                backgroundColor: isDark ? "rgba(229,115,115,0.1)" : undefined,
                color: isDark ? "#ef9a9a" : undefined,
                "& .MuiAlert-icon": { color: isDark ? "#ef9a9a" : undefined },
              }}
            >
              {errorMsg}
            </Alert>
          )}

          <Box sx={{ pb: 3 }} />
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default StripeFundWalletPopup;
